import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import {
  Search,
  Activity,
  Filter,
  LayoutDashboard,
  Bell,
  AlertTriangle,
  Settings,
  Users,
  FileText,
  FilterX,
  Clock,
  Gauge,
  GitBranch,
  Network,
  BarChart3,
  Moon,
  Sun,
  Compass,
  HelpCircle,
  Maximize2,
  FolderOpen,
  ArrowRight,
  KeyRound,
  Shield,
  BellRing,
  Database,
  HardDrive,
  Trash2,
  HeartPulse,
} from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { useTheme } from '../context/ThemeContext'
import { useHelp } from '../context/HelpContext'
import { useTour } from '../context/TourContext'
import { VigiaIcon } from './ai/VigiaIcon'
import { dashboardsApi } from '../api/dashboards'
import { alertsApi } from '../api/alerts'
import { workspacesApi } from '../api/workspaces'

// --- Fuzzy search ---

function fuzzyScore(query: string, text: string): number {
  if (!query) return 1
  const q = query.toLowerCase()
  const t = text.toLowerCase()

  // Exact start bonus
  if (t.startsWith(q)) return 100
  // Contains bonus
  if (t.includes(q)) return 80

  // Character-by-character match
  let qi = 0
  let score = 0
  let consecutive = 0
  let prevIdx = -2

  for (let ti = 0; ti < t.length && qi < q.length; ti++) {
    if (t[ti] === q[qi]) {
      score += 10
      // Consecutive bonus
      if (ti === prevIdx + 1) {
        consecutive++
        score += consecutive * 5
      } else {
        consecutive = 0
      }
      // Word boundary bonus
      if (ti === 0 || t[ti - 1] === ' ' || t[ti - 1] === '-' || t[ti - 1] === '/') {
        score += 15
      }
      prevIdx = ti
      qi++
    }
  }

  // All query chars must be found
  return qi === q.length ? score : 0
}

function scoreItem(query: string, label: string, description?: string): number {
  const labelScore = fuzzyScore(query, label)
  const descScore = description ? fuzzyScore(query, description) * 0.7 : 0
  return Math.max(labelScore, descScore)
}

// --- Types ---

type CommandCategory = 'pages' | 'settings' | 'dashboards' | 'alerts' | 'workspaces' | 'actions'

interface CommandItem {
  id: string
  category: CommandCategory
  label: string
  description?: string
  icon: React.ReactNode
  color?: string
  onSelect: () => void
}

export function CommandPalette() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { isDark, setTheme } = useTheme()
  const { openContextualHelp } = useHelp()
  const { startTour } = useTour()

  const [isOpen, setIsOpen] = useState(false)
  const [isClosing, setIsClosing] = useState(false)
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)

  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  // --- Open / Close ---

  const open = useCallback(() => {
    setIsOpen(true)
    setIsClosing(false)
    setQuery('')
    setActiveIndex(0)
  }, [])

  const close = useCallback(() => {
    if (!isOpen) return
    setIsClosing(true)
  }, [isOpen])

  const handleAnimationEnd = useCallback(() => {
    if (isClosing) {
      setIsOpen(false)
      setIsClosing(false)
    }
  }, [isClosing])

  // Ctrl+K / Cmd+K global listener
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        if (isOpen && !isClosing) {
          setIsClosing(true)
        } else if (!isOpen) {
          setIsOpen(true)
          setIsClosing(false)
          setQuery('')
          setActiveIndex(0)
        }
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [isOpen, isClosing])

  // CustomEvent listener for header hint button
  useEffect(() => {
    const handler = () => open()
    window.addEventListener('open-command-palette', handler)
    return () => window.removeEventListener('open-command-palette', handler)
  }, [open])

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => inputRef.current?.focus())
    }
  }, [isOpen])

  // --- Data sources ---

  const isAdmin = user?.role === 'Admin'

  // Pages (static)
  const pages = useMemo<CommandItem[]>(() => {
    const items: CommandItem[] = [
      { id: 'page-events', category: 'pages', label: t('common.events'), icon: <Search size={18} />, onSelect: () => navigate('/events') },
      { id: 'page-traces', category: 'pages', label: t('nav.traces'), icon: <GitBranch size={18} />, onSelect: () => navigate('/traces') },
      { id: 'page-services', category: 'pages', label: t('nav.services'), icon: <Network size={18} />, onSelect: () => navigate('/services') },
      { id: 'page-livetail', category: 'pages', label: t('nav.liveTail'), icon: <Activity size={18} />, onSelect: () => navigate('/live-tail') },
      { id: 'page-signals', category: 'pages', label: t('common.signals'), icon: <Filter size={18} />, onSelect: () => navigate('/signals') },
      { id: 'page-dashboards', category: 'pages', label: t('common.dashboards'), icon: <LayoutDashboard size={18} />, onSelect: () => navigate('/dashboards') },
      { id: 'page-alerts', category: 'pages', label: t('common.alerts'), icon: <Bell size={18} />, onSelect: () => navigate('/alerts') },
      { id: 'page-anomalies', category: 'pages', label: t('common.anomalies'), icon: <AlertTriangle size={18} />, onSelect: () => navigate('/anomalies') },
      { id: 'page-health-checks', category: 'pages', label: t('healthChecks.sidebar'), icon: <HeartPulse size={18} />, onSelect: () => navigate('/health-checks') },
      { id: 'page-settings', category: 'pages', label: t('common.settings'), icon: <Settings size={18} />, onSelect: () => navigate('/settings') },
    ]
    if (isAdmin) {
      items.push(
        { id: 'page-users', category: 'pages', label: t('common.users'), icon: <Users size={18} />, onSelect: () => navigate('/users') },
        { id: 'page-audit', category: 'pages', label: t('common.auditLogs'), icon: <FileText size={18} />, onSelect: () => navigate('/audit') },
        { id: 'page-ingestion', category: 'pages', label: t('ingestionRules.sidebar'), icon: <FilterX size={18} />, onSelect: () => navigate('/ingestion-rules') },
        { id: 'page-retention', category: 'pages', label: t('retentionPolicies.sidebar'), icon: <Clock size={18} />, onSelect: () => navigate('/retention-policies') },
        { id: 'page-metrics', category: 'pages', label: t('metrics.sidebar'), icon: <Gauge size={18} />, onSelect: () => navigate('/metrics') },
        { id: 'page-usage', category: 'pages', label: t('usage.sidebar'), icon: <BarChart3 size={18} />, onSelect: () => navigate('/usage') },
      )
    } else {
      if (user?.canManageIngestionRules) {
        items.push({ id: 'page-ingestion', category: 'pages', label: t('ingestionRules.sidebar'), icon: <FilterX size={18} />, onSelect: () => navigate('/ingestion-rules') })
      }
      if (user?.canManageRetention) {
        items.push({ id: 'page-retention', category: 'pages', label: t('retentionPolicies.sidebar'), icon: <Clock size={18} />, onSelect: () => navigate('/retention-policies') })
      }
      if (user?.canManageMetrics) {
        items.push({ id: 'page-metrics', category: 'pages', label: t('metrics.sidebar'), icon: <Gauge size={18} />, onSelect: () => navigate('/metrics') })
      }
    }
    return items
  }, [t, navigate, isAdmin, user?.canManageIngestionRules, user?.canManageRetention, user?.canManageMetrics])

  // Remote data - only fetch when palette is open
  const { data: dashboards } = useQuery({
    queryKey: ['dashboards'],
    queryFn: () => dashboardsApi.getDashboards(),
    enabled: isOpen,
  })

  const { data: alerts } = useQuery({
    queryKey: ['alerts'],
    queryFn: alertsApi.getAlerts,
    enabled: isOpen,
  })

  const { data: workspaces } = useQuery({
    queryKey: ['workspaces'],
    queryFn: () => workspacesApi.getWorkspaces(),
    enabled: isOpen,
  })

  // Build command items from remote data
  const dashboardItems = useMemo<CommandItem[]>(() =>
    (dashboards ?? []).map(d => ({
      id: `dash-${d.id}`,
      category: 'dashboards' as const,
      label: d.name,
      description: d.description,
      icon: <LayoutDashboard size={18} />,
      onSelect: () => navigate(`/dashboards/${d.id}`),
    })),
    [dashboards, navigate],
  )

  const alertItems = useMemo<CommandItem[]>(() =>
    (alerts ?? []).map(a => ({
      id: `alert-${a.id}`,
      category: 'alerts' as const,
      label: a.name,
      description: a.description,
      icon: <Bell size={18} />,
      onSelect: () => navigate(`/alerts?edit=${a.id}`),
    })),
    [alerts, navigate],
  )

  const workspaceItems = useMemo<CommandItem[]>(() =>
    (workspaces ?? []).map(w => ({
      id: `ws-${w.id}`,
      category: 'workspaces' as const,
      label: w.name,
      description: w.description,
      icon: <FolderOpen size={18} />,
      onSelect: () => navigate('/events'),
    })),
    [workspaces, navigate],
  )

  // Settings sub-sections (static, role-filtered)
  const { canWrite } = useAuth()
  const settingsItems = useMemo<CommandItem[]>(() => {
    const items: CommandItem[] = []
    if (canWrite) {
      items.push(
        { id: 'settings-apikeys', category: 'settings', label: t('settings.sections.apikeys.title'), description: t('settings.sections.apikeys.description'), icon: <KeyRound size={18} />, onSelect: () => navigate('/settings?section=apikeys') },
        { id: 'settings-notifications', category: 'settings', label: t('settings.sections.notificationChannels.title'), description: t('settings.sections.notificationChannels.description'), icon: <BellRing size={18} />, onSelect: () => navigate('/settings?section=notifications') },
      )
    }
    if (isAdmin) {
      items.push(
        { id: 'settings-security', category: 'settings', label: t('settings.sections.security.title'), description: t('settings.sections.security.description'), icon: <Shield size={18} />, onSelect: () => navigate('/settings?section=security') },
        { id: 'settings-valkey', category: 'settings', label: t('settings.sections.valkey.title'), description: t('settings.sections.valkey.description'), icon: <Database size={18} />, onSelect: () => navigate('/settings?section=valkey') },
        { id: 'settings-backups', category: 'settings', label: t('settings.sections.backups.title'), description: t('settings.sections.backups.description'), icon: <HardDrive size={18} />, onSelect: () => navigate('/settings?section=backups') },
        { id: 'settings-purge', category: 'settings', label: t('settings.sections.purgeLogs.title'), description: t('settings.sections.purgeLogs.description'), icon: <Trash2 size={18} />, onSelect: () => navigate('/settings?section=purge') },
      )
    }
    return items
  }, [t, navigate, isAdmin, canWrite])

  // Actions (static)
  const actions = useMemo<CommandItem[]>(() => [
    {
      id: 'action-theme',
      category: 'actions',
      label: t('commandPalette.toggleDarkMode'),
      icon: isDark ? <Sun size={18} /> : <Moon size={18} />,
      onSelect: () => setTheme(isDark ? 'light' : 'dark'),
    },
    {
      id: 'action-tour',
      category: 'actions',
      label: t('commandPalette.startTour'),
      icon: <Compass size={18} />,
      onSelect: () => startTour(),
    },
    {
      id: 'action-help',
      category: 'actions',
      label: t('commandPalette.openHelp'),
      icon: <HelpCircle size={18} />,
      onSelect: () => openContextualHelp(),
    },
    {
      id: 'action-focus',
      category: 'actions',
      label: t('commandPalette.toggleFocusMode'),
      icon: <Maximize2 size={18} />,
      onSelect: () => window.dispatchEvent(new CustomEvent('pcl-focus-mode-toggle')),
    },
    {
      id: 'action-ai',
      category: 'actions',
      label: t('commandPalette.openAiAssistant'),
      icon: <VigiaIcon size={18} />,
      onSelect: () => window.dispatchEvent(new CustomEvent('pcl-open-ai-chat')),
    },
  ], [t, isDark, setTheme, startTour, openContextualHelp])

  // --- Filtered + scored results ---

  const allItems = useMemo(
    () => [...pages, ...settingsItems, ...dashboardItems, ...alertItems, ...workspaceItems, ...actions],
    [pages, settingsItems, dashboardItems, alertItems, workspaceItems, actions],
  )

  const filtered = useMemo(() => {
    if (!query.trim()) return allItems
    return allItems
      .map(item => ({ item, score: scoreItem(query, item.label, item.description) }))
      .filter(x => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .map(x => x.item)
  }, [allItems, query])

  // Group by category for display
  const categoryOrder: CommandCategory[] = ['pages', 'settings', 'dashboards', 'alerts', 'workspaces', 'actions']

  const grouped = useMemo(() => {
    const map = new Map<CommandCategory, CommandItem[]>()
    for (const item of filtered) {
      const arr = map.get(item.category) ?? []
      arr.push(item)
      map.set(item.category, arr)
    }
    return categoryOrder.filter(c => map.has(c)).map(c => ({ category: c, items: map.get(c)! }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered])

  // Flat list for keyboard navigation
  const flatItems = useMemo(() => grouped.flatMap(g => g.items), [grouped])

  // Reset active index when results change
  useEffect(() => { setActiveIndex(0) }, [filtered])

  // --- Keyboard navigation ---

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex(i => (i + 1) % (flatItems.length || 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex(i => (i - 1 + (flatItems.length || 1)) % (flatItems.length || 1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (flatItems[activeIndex]) {
        flatItems[activeIndex].onSelect()
        close()
      }
    } else if (e.key === 'Escape') {
      e.preventDefault()
      close()
    }
  }

  // Scroll active item into view
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-index="${activeIndex}"]`)
    el?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex])

  // --- Render ---

  if (!isOpen) return null

  let flatIndex = -1

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[20vh]" onClick={close}>
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        style={{ animation: isClosing ? 'cmdBackdropOut 200ms ease-in forwards' : 'cmdBackdropIn 200ms ease-out' }}
      />

      {/* Modal */}
      <div
        className="relative w-full max-w-lg mx-4 bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden"
        style={{
          transformOrigin: 'top center',
          animation: isClosing ? 'cmdPaletteOut 200ms ease-in forwards' : 'cmdPaletteIn 250ms cubic-bezier(0.16, 1, 0.3, 1)',
        }}
        onClick={e => e.stopPropagation()}
        onKeyDown={handleKeyDown}
        onAnimationEnd={handleAnimationEnd}
      >
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <Search size={20} className="text-gray-400 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={t('commandPalette.placeholder')}
            className="flex-1 bg-transparent text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 text-sm outline-none"
          />
          <button
            onClick={close}
            className="shrink-0 text-xs text-gray-400 dark:text-gray-500 border border-gray-300 dark:border-gray-600 rounded px-1.5 py-0.5 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            ESC
          </button>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-[50vh] overflow-y-auto overscroll-contain">
          {flatItems.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
              {t('commandPalette.noResults')}
            </div>
          ) : (
            grouped.map(group => (
              <div key={group.category}>
                {/* Category header */}
                <div className="sticky top-0 bg-gray-50 dark:bg-gray-800/80 backdrop-blur-sm px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {t(`commandPalette.categories.${group.category}`)}
                </div>
                {group.items.map(item => {
                  flatIndex++
                  const idx = flatIndex
                  const isActive = idx === activeIndex
                  return (
                    <button
                      key={item.id}
                      data-index={idx}
                      onClick={() => { item.onSelect(); close() }}
                      onMouseEnter={() => setActiveIndex(idx)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                        isActive
                          ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                      }`}
                    >
                      <span
                        className={`shrink-0 ${isActive ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'}`}
                        style={item.color ? { color: item.color } : undefined}
                      >
                        {item.icon}
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="text-sm font-medium truncate block">{item.label}</span>
                        {item.description && (
                          <span className="text-xs text-gray-400 dark:text-gray-500 truncate block">{item.description}</span>
                        )}
                      </span>
                      {isActive && item.category !== 'actions' && (
                        <span className="shrink-0 text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1">
                          {t('commandPalette.goTo')}
                          <ArrowRight size={12} />
                        </span>
                      )}
                    </button>
                  )
                })}
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-4 px-4 py-2 border-t border-gray-200 dark:border-gray-700 text-[11px] text-gray-400 dark:text-gray-500">
          <span className="flex items-center gap-1">
            <kbd className="px-1 py-0.5 rounded border border-gray-300 dark:border-gray-600 font-mono text-[10px]">&uarr;&darr;</kbd>
            {t('commandPalette.navigate')}
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1 py-0.5 rounded border border-gray-300 dark:border-gray-600 font-mono text-[10px]">&crarr;</kbd>
            {t('commandPalette.select')}
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1 py-0.5 rounded border border-gray-300 dark:border-gray-600 font-mono text-[10px]">esc</kbd>
            {t('commandPalette.close')}
          </span>
        </div>
      </div>

      <style>{`
        @keyframes cmdPaletteIn {
          0% {
            opacity: 0;
            transform: translateY(-20vh) scaleY(0.35) scaleX(0.92);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scaleY(1) scaleX(1);
          }
        }
        @keyframes cmdPaletteOut {
          0% {
            opacity: 1;
            transform: translateY(0) scaleY(1) scaleX(1);
          }
          100% {
            opacity: 0;
            transform: translateY(-20vh) scaleY(0.35) scaleX(0.92);
          }
        }
        @keyframes cmdBackdropIn {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes cmdBackdropOut {
          0% { opacity: 1; }
          100% { opacity: 0; }
        }
      `}</style>
    </div>
  )
}
